import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  MaterialIcons,
  Checkbox,
  Dialog,
  DialogActions,
  TextField,
  Body,
} from '@iclinic/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import { useRifm } from 'rifm';

import * as S from './styles';
import {
  getClinicId,
  isOptinConfirmationModalOpen,
  isOptInAccepted,
} from 'features/free-samples/state/opt-in-confirmation/selectors';
import {
  clearOptInAddressData,
  closeConfirmationModal,
  fetchClinicAddress,
  openAddressModal,
  openSuccessModal,
  sendIntercomEvent,
  setOptInAddressData,
  setOptInPersonalData,
} from 'features/free-samples/state/opt-in-confirmation';
import { initialFormData } from './form.normalize';
import { validationForm } from './validation';
import { cepFormatter, cpfFormatter } from 'shared/utils/formatters';

const { Truck, Gift } = MaterialIcons;

const OptInConfirmationModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isOptinConfirmationModalOpen);
  const isOptInStatusAccepted = useSelector(isOptInAccepted);
  const clinicId = useSelector(getClinicId);

  if (isOptInStatusAccepted) {
    dispatch(openSuccessModal());
    dispatch(closeConfirmationModal());
  }

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema: validationForm,

    onSubmit: (formData) => {
      dispatch(
        setOptInPersonalData({
          cpf: formData.cpf,
          crm: formData.crm,
          zip_code: formData.zip_code,
          existing_clinic_address: formData.existing_clinic_address,
        }),
      );

      if (formData.existing_clinic_address && clinicId) {
        dispatch(clearOptInAddressData());
        dispatch(fetchClinicAddress(Number(clinicId)));
      } else {
        dispatch(
          setOptInAddressData({
            phone_number: '',
            zip_code: formData.zip_code,
            address: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            is_clinic_address: false,
          }),
        );
      }

      dispatch(openAddressModal());
      dispatch(sendIntercomEvent('started_not_completed'));
    },
  });

  const {
    errors,
    touched,
    values,
    handleChange,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = formik;

  const { value: cpfValue, onChange: onChangeCpf } = useRifm({
    value: values.cpf,
    onChange: (value: string) => {
      setFieldValue('cpf', value);
    },

    format: cpfFormatter,
  });

  const { value: zipCode, onChange: onChangeZipCode } = useRifm({
    value: values.zip_code,
    onChange: (value: string) => {
      setFieldValue('zip_code', value);
    },

    format: cepFormatter,
  });

  const handleZipCodeCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { checked } = event.target;
    setFieldValue('existing_clinic_address', checked);

    if (checked) {
      setFieldValue('zip_code', '');
      setFieldError('zip_code', undefined);
      setFieldTouched('zip_code', false);
    } else {
      dispatch(clearOptInAddressData());
    }
  };

  const handleCloseModal = () => {
    dispatch(closeConfirmationModal());
    formik.resetForm();
  };

  if (isOpen && !isOptInStatusAccepted) {
    dispatch(sendIntercomEvent('click_menu'));
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <Dialog open={isOpen} onClose={handleCloseModal} maxWidth="sm">
          <S.DialogHeader
            onClose={handleCloseModal}
            title="Receba amostras grátis"
          />

          <S.DialogContent>
            <S.CardContainer>
              <S.Card>
                <S.IconWrapper>
                  <Gift />
                </S.IconWrapper>
                Serviço totalmente gratuito
              </S.Card>
              <S.Card>
                <S.IconWrapper>
                  <Truck />
                </S.IconWrapper>
                Entrega prática e dinâmica
              </S.Card>
            </S.CardContainer>

            <Body variant="xs">
              Em parceria com a RX Pro, agora você pode se cadastrar para
              receber amostras grátis e ter acesso a conteúdos técnicos e
              conexão direta ao laboratório.
            </Body>

            <S.Body variant="xxs">
              Informe o endereço atualizado da sua clínica para confirmar o
              interesse em receber as amostras. É muito importante que tenha
              alguém no local para recebimento.
            </S.Body>

            <S.FieldsWrapper>
              <TextField
                id="cpf"
                label="CPF"
                name="cpf"
                placeholder="000.000.000-00"
                value={cpfValue}
                onChange={onChangeCpf}
                error={!!(errors.cpf && touched.cpf)}
                helperText={touched.cpf && errors.cpf}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]',
                }}
              />

              <TextField
                id="crm"
                label="CRM"
                name="crm"
                placeholder="Ex: 1234567"
                value={values.crm}
                onChange={handleChange}
                error={!!(errors.crm && touched.crm)}
                helperText={touched.crm && errors.crm}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]',
                }}
              />
            </S.FieldsWrapper>

            <S.CepWrapper>
              <TextField
                id="zip_code"
                name="zip_code"
                label="CEP"
                placeholder="Digite aqui o CEP para entrega na sua clínica"
                value={zipCode}
                onChange={onChangeZipCode}
                error={!!(errors.zip_code && touched.zip_code)}
                helperText={touched.zip_code && errors.zip_code}
                disabled={values.existing_clinic_address}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]',
                }}
              />

              <S.CepHelper
                href="https://buscacepinter.correios.com.br/"
                target="_blank"
                rel="noreferrer"
              >
                Não sei meu CEP
              </S.CepHelper>
            </S.CepWrapper>

            <S.CheckboxWrapper>
              <Checkbox
                id="existing_clinic_address"
                name="existing_clinic_address"
                label="Usar endereço cadastrado no iClinic"
                checked={values.existing_clinic_address}
                onChange={handleZipCodeCheckboxChange}
              />
            </S.CheckboxWrapper>
          </S.DialogContent>
          <DialogActions>
            <S.ButtonStyled color="transparent" onClick={handleCloseModal}>
              Agora não
            </S.ButtonStyled>

            <Button
              onClick={() => formik.handleSubmit()}
              data-testid="freesamples-modal-cta"
              data-ga="free_samples_opt_in"
              data-sp="free_samples_opt_in"
            >
              Confirmar meu endereço
            </Button>
          </DialogActions>
        </Dialog>
      </Form>
    </FormikProvider>
  );
};

export default OptInConfirmationModal;
